import React, { useCallback, useState, useEffect } from "react";
import styles from "./index.module.css";
import PanelHeader from "../components/PanelHeader";
import TabBar from "../components/TabBar";
import OrderHistory from "./Components/OrderHistory";
import CreateOrder from "./Components/CreateOrder";
import { useIboCart } from "./hooks/useIboCart";
import { useOrderCart } from "./hooks/useOrderCart";
import { ibos as ibosApi } from "../../shared/api";

const tabs = [
  { key: "history", label: "Order History" },
  { key: "create", label: "Create Order" },
];

// The selected IBO is convenience UI state (which IBO the rep is working on),
// not server data. It's kept in sessionStorage so it survives a refresh or a
// hop to another admin page, but a new browser session starts on "No IBO
// selected" (Wrike "Unable to get back to the No IBO selected screen":
// localStorage restored the last IBO forever). The header's x clears it.
const IBO_STORAGE_KEY = "wwg_order_mgmt_selected_ibo";

// Deep value comparison for the CRM re-read below. A shallow compare is not
// enough: `fresh` is parsed from JSON, so nested values like `users` are always
// new arrays and would always look "changed". Serialization is safe here because
// both sides originate from the same merge/parse, so key order is stable; if it
// ever isn't, we just fall back to the old behaviour of updating.
function sameIboValue(a, b) {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch (err) {
    return false;
  }
}

function loadSelectedIbo() {
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      // Drop the copy older builds left in localStorage.
      if (window.localStorage) window.localStorage.removeItem(IBO_STORAGE_KEY);
      const raw = window.sessionStorage.getItem(IBO_STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    }
  } catch (err) {
    // Corrupt/blocked storage — fall back to no selection.
  }
  return null;
}

export default function OrderManagement() {
  const [activeTab, setActiveTab] = useState(tabs[0].key);
  const [selectedIbo, setSelectedIbo] = useState(loadSelectedIbo);

  // The IBO search box is conceptually one field shared by both tabs — typing
  // in Create Order should show the same text in Order History and vice versa.
  // The query lives here in the parent so both tabs' useIboSearch hooks read and
  // write the same value.
  const [iboQuery, setIboQuery] = useState("");

  // Two SEPARATE carts (per the Figma + backend): the IBO's own cart (items the
  // rep pushes for the IBO to check out themselves) and the admin "New Order"
  // cart (a brand-new order the admin places on the IBO's behalf, with Order
  // Options). Both are lifted here so the shared IBO selection/search feed them
  // and so Re-Ship (from Order History) can seed the order cart and switch tabs.
  const iboCart = useIboCart(selectedIbo);
  const orderCart = useOrderCart(selectedIbo);

  // Which destination the Create Order tab is showing: the admin "New Order"
  // cart or the IBO's cart.
  const [createMode, setCreateMode] = useState("new_order");

  // Bumped whenever Create Order places a new order so the (always-mounted)
  // Order History tab refetches its list instead of showing a stale one.
  const [ordersVersion, setOrdersVersion] = useState(0);
  const handleOrderPlaced = useCallback(() => {
    setOrdersVersion((v) => v + 1);
    // The order that was just placed is the thing the rep wants to see next,
    // and leaving them on an emptied Create Order form reads as "nothing
    // happened". Same move as Re-Ship makes in the other direction.
    setActiveTab("history");
  }, []);

  // Re-Ship: seed the admin order cart with the chosen order line(s) (Charge
  // Card On File stays OFF so the customer is never re-billed), open the New
  // Order destination, and jump to Create Order so the rep can review + place a
  // replacement order.
  const handleReship = useCallback(
    (items, sourceOrderId) => {
      orderCart.seed(
        (items || []).map((item) => {
          // Admin orders are priced at retail (the BFF re-prices every line at
          // retail on create), so seed the cart at retail rather than the PM
          // price the member originally paid — otherwise the cart shows one
          // figure and the saved order records another.
          const retail = Number(item.retailPrice);
          return {
            ...item,
            price: Number.isFinite(retail) && retail > 0 ? retail : item.price,
            sourceOrderId: sourceOrderId ?? item.sourceOrderId ?? null,
          };
        }),
        { chargeCardOnFile: false }
      );
      setCreateMode("new_order");
      setActiveTab("create");
    },
    [orderCart]
  );

  useEffect(() => {
    try {
      if (typeof window === "undefined" || !window.sessionStorage) return;
      if (selectedIbo) {
        window.sessionStorage.setItem(
          IBO_STORAGE_KEY,
          JSON.stringify(selectedIbo)
        );
      } else {
        window.sessionStorage.removeItem(IBO_STORAGE_KEY);
      }
    } catch (err) {
      // Quota/serialization failure — keep the in-memory selection.
    }
  }, [selectedIbo]);

  // The search hit (and anything restored from localStorage) is a snapshot of
  // the directory document. The people on the account are resolved from CRM,
  // which is the only place the contact id the cart push authorises against
  // exists — so re-read the IBO once it's selected rather than acting on
  // whatever roster was embedded in the search payload.
  const selectedIboId = selectedIbo?.id ?? null;
  useEffect(() => {
    if (!selectedIboId) return undefined;
    let cancelled = false;
    ibosApi
      .get(selectedIboId)
      .then((fresh) => {
        if (cancelled || !fresh || fresh.id !== selectedIboId) return;
        setSelectedIbo((prev) => {
          if (!prev || prev.id !== selectedIboId) return prev;
          const merged = { ...prev, ...fresh };
          // On a revisit the CRM returns what the persisted snapshot already
          // has, so this merge is usually a no-op by value. Returning a new
          // object anyway would still change `selectedIbo`'s identity, and that
          // identity is a dependency of Order History's fetch effect *and* its
          // "reset to page 1" effect — costing a duplicate /orders request and
          // yanking the rep back to page 1 if they had already paged or
          // searched. Only publish a new object when something really changed.
          return sameIboValue(prev, merged) ? prev : merged;
        });
      })
      .catch(() => {
        // Keep the search snapshot; the push itself re-resolves server-side and
        // will report a mismatch rather than write to the wrong cart.
      });
    return () => {
      cancelled = true;
    };
  }, [selectedIboId]);

  return (
    <div className={styles.container}>
      <PanelHeader title="WWG Store - Order Management" />

      <TabBar tabs={tabs} activeKey={activeTab} onChange={setActiveTab} />

      <div className={styles.content}>
        <div className={styles.contentInner}>
          {/* Both tabs stay mounted so local state (IBO search query, scroll
              position, etc.) survives switching tabs. Only visibility changes. */}
          <div style={{ display: activeTab === "history" ? "block" : "none" }}>
            <OrderHistory
              selectedIbo={selectedIbo}
              onSelectIbo={setSelectedIbo}
              onReship={handleReship}
              iboQuery={iboQuery}
              onIboQueryChange={setIboQuery}
              refreshToken={ordersVersion}
            />
          </div>
          <div style={{ display: activeTab === "create" ? "block" : "none" }}>
            <CreateOrder
              selectedIbo={selectedIbo}
              onSelectIbo={setSelectedIbo}
              iboCart={iboCart}
              orderCart={orderCart}
              activeMode={createMode}
              onModeChange={setCreateMode}
              iboQuery={iboQuery}
              onIboQueryChange={setIboQuery}
              onOrderPlaced={handleOrderPlaced}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
